import { BRAND_SOURCE_KEY, BRAND_NAME, BRAND_PERSON_NAME } from "@/lib/brandConfig";
import { getDataFastStripeMetadata } from "@/lib/datafast";
/**
 * SorprendemeStripeTemplate - International Donation Page (Stripe)
 * 3-step wizard: Amount → Message → Review & Pay
 */

import { StripeCheckoutForm } from "@/components/StripeCheckoutForm";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { STRIPE_PUBLISHABLE_KEY, STRIPE_APPEARANCE } from "@/config/stripe";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { HEADER_PROFILE_IMAGE_URL } from "@/config/cloudflare";
import {
  trackDonationPageViewPostHog,
  trackDonationAmountSelectedPostHog,
  trackDonationFormSubmittedPostHog,
  trackDonationPaymentSuccessPostHog,
  trackDonationPaymentErrorPostHog,
  trackDonationCheckoutStartedPostHog,
} from "@/lib/posthogAnalytics";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getRandomBackground } from "@/lib/sorprende-backgrounds";
import { ArrowLeft, Check, Loader2 } from "lucide-react";

// Stripe singleton
let _stripePromise: Promise<Stripe | null> | null = null;
function getStripe(): Promise<Stripe | null> | null {
  if (!STRIPE_PUBLISHABLE_KEY) return null;
  if (!_stripePromise) _stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  return _stripePromise;
}

// Amount options with emoji + gift tier label
const AMOUNTS: { value: number; emoji: string; label: string; disabled?: boolean }[] = [
  { value: 35, emoji: "💋", label: "Mini" },
  { value: 50, emoji: "❤️‍🔥", label: "Classic" },
  { value: 70, emoji: "❤️‍🔥🔥", label: "Special" },
  { value: 99, emoji: "🔥", label: "Premium" },
  { value: 150, emoji: "🔥🔥", label: "Elite" },
  { value: 200, emoji: "🔥🔥🔥", label: "VIP" },
];
// Minimum amount for USD - matches lowest enabled tier (Classic)
const MIN_AMOUNT = 50;

type WizardStep = "amount" | "details" | "review" | "checkout" | "success" | "error" | "pending";

// ── Confetti (pure Framer Motion, no deps) ───────────────────
function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 42 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 0.9,
        duration: 1.9 + Math.random() * 1.6,
        size: 5 + Math.random() * 9,
        color: ["#FF6B9D", "#C850C0", "#FFBD59", "#5C7CFA", "#63E6BE", "#FF9F43", "#fff"][
          Math.floor(Math.random() * 7)
        ],
        circle: Math.random() > 0.45,
        rotation: Math.random() * 720 - 360,
      })),
    []
  );
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {pieces.map((p) => (
        <motion.div
          key={p.id}
          style={{
            position: "absolute",
            top: "-5%",
            left: `${p.x}%`,
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.circle ? "50%" : 2,
          }}
          animate={{ y: "115vh", rotate: p.rotation, opacity: [1, 1, 1, 0] }}
          transition={{ duration: p.duration, delay: p.delay, ease: "linear" }}
        />
      ))}
    </div>
  );
}

// ── Thin top progress bar ─────────────────────────────────────
function ProgressBar({ step }: { step: 1 | 2 | 3 }) {
  return (
    <motion.div
      className="fixed top-0 left-0 z-50 h-[3px] rounded-r-full"
      animate={{ width: `${(step / 3) * 100}%` }}
      transition={{ duration: 0.42, ease: "easeInOut" }}
      style={{ background: "linear-gradient(to right, #ec4899, #a855f7)" }}
    />
  );
}

// ── Animated step pills ───────────────────────────────────────
function StepDots({ current }: { current: 1 | 2 | 3 }) {
  return (
    <div className="flex items-center gap-1.5">
      {[1, 2, 3].map((n) => (
        <motion.div
          key={n}
          animate={{
            width: n === current ? 28 : 8,
            backgroundColor:
              n < current ? "#22c55e" : n === current ? "#ffffff" : "rgba(255,255,255,0.22)",
          }}
          transition={{ duration: 0.32, ease: "easeInOut" }}
          style={{ height: 8, borderRadius: 4, flexShrink: 0 }}
        />
      ))}
    </div>
  );
}

// ── Slide animation ───────────────────────────────────────────
const slideVariants = {
  enter: (d: number) => ({ x: d > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (d: number) => ({ x: d > 0 ? -60 : 60, opacity: 0 }),
};
const slideT = { type: "tween" as const, duration: 0.26, ease: "easeInOut" as const };

// ── Background ────────────────────────────────────────────────
function Background({ src }: { src: string | null }) {
  return (
    <>
      {src ? (
        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2 }}
          className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat will-change-[opacity,transform]"
          style={{ 
            backgroundImage: `url("${src}")`,
            transform: 'translateZ(0)',
          }}
        />
      ) : (
        <div
          className="fixed inset-0 z-0"
          style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
        />
      )}
      <div className="fixed inset-0 z-[1] bg-black/58 backdrop-blur-[3px] will-change-auto" />
    </>
  );
}

export default function SorprendemeStripeTemplate() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState<WizardStep>("amount");
  const [dir, setDir] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [bgImage] = useState(() => getRandomBackground());

  const [formData, setFormData] = useState({
    selectedAmount: "",
    customAmount: "",
    message: "",
    name: "",
    email: "",
  });

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [donationId, setDonationId] = useState<string | null>(null);

  useEffect(() => { trackDonationPageViewPostHog(); }, []);

  // Handle Stripe redirect return
  useEffect(() => {
    const redirectStatus = searchParams.get("redirect_status");
    const paymentIntent = searchParams.get("payment_intent");
    if (redirectStatus === "succeeded" && paymentIntent) {
      setStep("success");
      setSearchParams({});
      trackDonationPaymentSuccessPostHog(getFinalAmount(), paymentIntent);
    } else if (redirectStatus === "failed") {
      setStep("error");
      trackDonationPaymentErrorPostHog(getFinalAmount(), "Payment failed");
      setTimeout(() => { setSearchParams({}); setStep("amount"); }, 3000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const getFinalAmount = () => {
    if (formData.customAmount) return parseFloat(formData.customAmount);
    if (formData.selectedAmount) return parseFloat(formData.selectedAmount);
    return 0;
  };

  const isAmountValid = () => getFinalAmount() >= MIN_AMOUNT;

  const goTo = (target: WizardStep, direction = 1) => {
    setDir(direction);
    setStep(target);
  };

  const handleAmountTap = (value: number) => {
    setFormData((prev) => ({ ...prev, selectedAmount: value.toString(), customAmount: "" }));
    trackDonationAmountSelectedPostHog(value, false);
    setTimeout(() => goTo("details"), 480);
  };

  const handleCustomChange = (v: string) => {
    const clean = v.replace(/[^\d.]/g, "");
    setFormData((prev) => ({ ...prev, customAmount: clean, selectedAmount: "" }));
    if (clean && parseFloat(clean) >= 10) trackDonationAmountSelectedPostHog(parseFloat(clean), true);
  };

  // Called from the Review step when user taps "Pay"
  const handleConfirm = async () => {
    const amount = getFinalAmount();
    if (amount < MIN_AMOUNT) return;
    if (formData.email.trim() && !formData.email.includes("@")) {
      toast({ title: "📧 Invalid email", description: "Please check your email address", variant: "destructive" });
      return;
    }

    setProcessing(true);
    trackDonationFormSubmittedPostHog(amount, !!formData.name.trim(), formData.message.length);

    try {
      let donId = `donation_${Date.now()}`;
      try {
        const res = await fetch("/api/donations/save-message", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount,
            currency: "USD",
            message: formData.message.trim() || "No message",
            donor_name: formData.name.trim() || "Anonymous",
            donor_email: formData.email.trim() || null,
          }),
        });
        if (res.ok) {
          const d = await res.json() as { donation_id: string };
          donId = d.donation_id;
        }
      } catch { /* non-blocking */ }

      setDonationId(donId);

      const res = await fetch("/api/create-payment-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: Math.round(amount * 100),
          currency: "usd",
          packId: "sorpresa",
          buyerEmail: formData.email.trim() || undefined,
          ...getDataFastStripeMetadata(),
          metadata: {
            source: BRAND_SOURCE_KEY,
            type: "gift",
            product_type: "gift",
            donation_id: donId,
            donor_name: formData.name.trim() || "Anonymous",
            donor_email: formData.email.trim() || null,
            page: "sorprendeme_international",
            ...getDataFastStripeMetadata(),
          },
        }),
      });

      if (!res.ok) throw new Error("Couldn't connect to payment system. Please try again.");

      const data = await res.json() as { clientSecret: string };
      setClientSecret(data.clientSecret);
      trackDonationCheckoutStartedPostHog(amount, data.clientSecret);
      goTo("checkout");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      trackDonationPaymentErrorPostHog(getFinalAmount(), msg);
      toast({ title: "😔 Something went wrong", description: msg, variant: "destructive" });
    } finally {
      setProcessing(false);
    }
  };

  const finalAmount = getFinalAmount();
  const selectedConfig = AMOUNTS.find((a) => a.value.toString() === formData.selectedAmount);
  const stepNum: 1 | 2 | 3 = step === "amount" ? 1 : step === "details" ? 2 : 3;

  // ── Checkout ────────────────────────────────────────────────
  if (step === "checkout" && clientSecret) {
    const stripePromise = getStripe();
    if (!stripePromise) {
      return (
        <div className="min-h-[100dvh] flex items-center justify-center text-white bg-black">
          Stripe not configured
        </div>
      );
    }
    
    // Memoize the final amount to avoid recalculations
    const checkoutAmount = getFinalAmount() * 100;
    
    return (
      <div 
        className="h-dvh flex flex-col bg-black text-white"
        style={{
          // Safari iOS & Android: Prevent layout issues
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Elements 
          stripe={stripePromise} 
          options={{ 
            clientSecret, 
            appearance: STRIPE_APPEARANCE,
            loader: 'auto',
          }}
        >
          <div className="flex-1 flex flex-col min-h-0">
            <StripeCheckoutForm
              amount={checkoutAmount}
              currency="usd"
              packName="Gift - Surprise Me ✨"
              language="en"
              returnUrl={`${window.location.origin}/sorprendeme?redirect_status=succeeded`}
              onSuccess={(paymentIntentId) => {
                setStep("success");
                trackDonationPaymentSuccessPostHog(getFinalAmount(), paymentIntentId || donationId);
              }}
              onError={(error) => {
                setStep("error");
                trackDonationPaymentErrorPostHog(getFinalAmount(), error);
                setTimeout(() => setStep("amount"), 3000);
              }}
              onBack={() => goTo("review", -1)}
              flowSurface="sorprendeme_stripe"
            />
          </div>
        </Elements>
      </div>
    );
  }

  // ── Status screens ──────────────────────────────────────────
  if (step === "success" || step === "error" || step === "pending") {
    const cfg = {
      success: {
        icon: "💖",
        title: "I received your gift!",
        msg: "Your message and support mean the world to me",
        grad: "from-pink-500/20 to-purple-500/20",
        border: "border-pink-400/40",
      },
      error: {
        icon: "😔",
        title: "Oops, something failed",
        msg: "I couldn't process your payment. Please try again 💔",
        grad: "from-red-500/20 to-pink-500/20",
        border: "border-red-400/40",
      },
      pending: {
        icon: "⏳",
        title: "Processing your gift",
        msg: "Your payment is being processed. It'll be ready soon! 🙏",
        grad: "from-yellow-500/20 to-orange-500/20",
        border: "border-yellow-400/40",
      },
    }[step];

    return (
      <div
        className="min-h-[100dvh] flex items-center justify-center p-5 relative overflow-hidden text-white font-body lining-nums tabular-nums"
      >
        <Background src={bgImage} />
        {step === "success" && <Confetti />}

        <motion.div
          initial={{ scale: 0.88, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          className="max-w-sm w-full space-y-5 relative z-20"
        >
          {/* Profile */}
          <div className="flex justify-center">
            <div className="relative">
              <motion.img
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: "spring" }}
                src={HEADER_PROFILE_IMAGE_URL}
                alt={BRAND_PERSON_NAME}
                className="w-20 h-20 rounded-full border-4 border-white/20 shadow-2xl object-cover"
              />
              {step === "success" && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5, type: "spring" }}
                  className="absolute -bottom-1.5 -right-1.5 w-7 h-7 bg-green-500 rounded-full border-2 border-black flex items-center justify-center"
                >
                  <Check size={14} strokeWidth={3} />
                </motion.div>
              )}
            </div>
          </div>

          {/* Status card */}
          <motion.div
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.12 }}
            className={`bg-gradient-to-br ${cfg.grad} backdrop-blur-2xl rounded-3xl border-2 ${cfg.border} p-7 text-center space-y-5 shadow-2xl`}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.28, type: "spring", stiffness: 200 }}
              className="mx-auto w-20 h-20 bg-black/35 rounded-full flex items-center justify-center border border-white/10"
            >
              <motion.span
                animate={step === "pending" ? { rotate: 360 } : {}}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="text-5xl"
              >
                {cfg.icon}
              </motion.span>
            </motion.div>

            <div className="space-y-2">
              <h2 className="text-2xl font-black">{cfg.title}</h2>
              <p className="text-white/75 leading-relaxed text-sm">{cfg.msg}</p>
            </div>

            {step === "success" && (
              <motion.div
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="space-y-4"
              >
                {formData.message && (
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.65 }}
                    className="bg-pink-500/10 border border-pink-500/25 rounded-2xl p-4 text-center space-y-2"
                  >
                    <p className="text-pink-300 text-[10px] font-bold uppercase tracking-widest">✨ Your message</p>
                    <p className="text-white text-sm leading-relaxed font-medium">
                      &ldquo;{formData.message}&rdquo;
                    </p>
                    {formData.name && <p className="text-white/45 text-xs italic">— {formData.name}</p>}
                  </motion.div>
                )}

                {formData.email && (
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-2.5 flex items-center justify-center gap-2">
                    <Check size={14} className="text-blue-400" />
                    <p className="text-blue-300 text-xs">Confirmation sent to {formData.email}</p>
                  </div>
                )}

                <Button
                  onClick={() => {
                    setStep("amount");
                    setFormData({ selectedAmount: "", customAmount: "", message: "", name: "", email: "" });
                  }}
                  className="w-full h-12 bg-gradient-to-r from-white to-pink-100 text-black font-bold rounded-xl shadow-xl hover:from-pink-100 hover:to-white transition-all"
                >
                  🎁 Send another gift ✨
                </Button>
                <p className="text-white/35 text-xs">Gracias por estar siempre ahí 🙏</p>
              </motion.div>
            )}
          </motion.div>

          <p className="text-center text-white/45 text-sm">
            With love, <span className="text-pink-400 font-medium">{BRAND_NAME}</span> 💕
          </p>
        </motion.div>
      </div>
    );
  }

  // ── Wizard ────────────────────────────────────────────────────
  return (
    <div
      className="min-h-[100dvh] text-white flex flex-col relative overflow-hidden font-body lining-nums tabular-nums"
    >
      <Background src={bgImage} />
      <ProgressBar step={stepNum} />

      <div className="relative z-20 flex-1 flex flex-col items-center justify-center px-4 py-7">
        <div className="w-full max-w-sm flex flex-col gap-4">

          {/* ── Top bar: back + step pills ── */}
          <div className="flex items-center justify-between">
            <motion.button
              animate={{
                opacity: step === "amount" ? 0 : 1,
                pointerEvents: step === "amount" ? "none" : "auto",
              }}
              transition={{ duration: 0.2 }}
              onClick={() =>
                step === "details"
                  ? goTo("amount", -1)
                  : step === "review"
                  ? goTo("details", -1)
                  : undefined
              }
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors touch-manipulation"
              aria-label="Go back"
            >
              <ArrowLeft size={16} />
            </motion.button>

            <StepDots current={stepNum} />
            <div className="w-9" />
          </div>

          {/* ── Profile ── */}
          <motion.div
            initial={{ y: -8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.06 }}
            className="flex flex-col items-center gap-1.5"
          >
            <div className="relative">
              <img
                src={HEADER_PROFILE_IMAGE_URL}
                alt={BRAND_PERSON_NAME}
                className="w-14 h-14 rounded-full border-2 border-white/25 shadow-xl object-cover"
              />
              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-black" />
            </div>
            <p className="text-white/45 text-[10px] font-semibold tracking-widest uppercase">{BRAND_PERSON_NAME}</p>
          </motion.div>

          {/* ── Animated step content ── */}
          <div className="relative" style={{ overflow: "hidden" }}>
            <AnimatePresence mode="wait" custom={dir}>

              {/* ─── STEP 1: Amount ─── */}
              {step === "amount" && (
                <motion.div
                  key="amount"
                  custom={dir}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={slideT}
                  className="space-y-4"
                >
                  <div className="text-center space-y-1">
                    <h1 className="text-[22px] font-black leading-tight">Choose your gift 🎀</h1>
                    <p className="text-white/45 text-sm">Your support means the world to me</p>
                  </div>

                  {/* Amount grid with emoji + label */}
                  <div className="grid grid-cols-3 gap-2">
                    {AMOUNTS.map((a) => {
                      const selected = formData.selectedAmount === a.value.toString();
                      const isDisabled = a.value < MIN_AMOUNT || !!a.disabled;
                      return (
                        <motion.button
                          key={a.value}
                          type="button"
                          disabled={isDisabled}
                          onClick={() => handleAmountTap(a.value)}
                          whileTap={{ scale: isDisabled ? 1 : 0.88 }}
                          animate={selected ? { scale: [1, 1.08, 1.02] } : { scale: 1 }}
                          transition={{ duration: 0.22 }}
                          className={`relative flex flex-col items-center gap-0.5 py-3.5 px-1 rounded-2xl border-2 transition-all touch-manipulation ${
                            isDisabled
                              ? "bg-white/5 border-white/10 opacity-40 cursor-not-allowed"
                              : selected
                                ? "bg-gradient-to-br from-pink-500 to-purple-600 border-pink-400 shadow-lg shadow-pink-500/35"
                                : "bg-white/10 border-white/20 hover:bg-white/15 hover:border-white/35 active:bg-white/20"
                          }`}
                        >
                          {selected && (
                            <motion.div
                              initial={{ scale: 0, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-black shadow-sm z-10"
                            >
                              <Check size={10} strokeWidth={3.5} />
                            </motion.div>
                          )}
                          <span className="text-xl leading-none">{a.emoji}</span>
                          <span className="text-[11px] font-black text-white leading-none mt-0.5">
                            ${a.value}
                          </span>
                        </motion.button>
                      );
                    })}
                  </div>

                  {/* Custom amount */}
                  <div className="space-y-2">
                    <p className="text-white/38 text-xs text-center font-medium">or a custom amount</p>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 font-bold text-base pointer-events-none">
                        $
                      </span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="Custom amount"
                        value={formData.customAmount}
                        onChange={(e) => handleCustomChange(e.target.value)}
                        className="pl-8 pr-11 h-12 bg-white/10 border-2 border-white/20 text-white placeholder:text-white/28 focus:border-pink-400 focus:ring-1 focus:ring-pink-400/25 rounded-xl font-semibold text-base"
                      />
                      <AnimatePresence>
                        {formData.customAmount && parseFloat(formData.customAmount) >= MIN_AMOUNT && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-sm"
                          >
                            <Check size={12} strokeWidth={3} />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    <AnimatePresence mode="wait">
                      {formData.customAmount && parseFloat(formData.customAmount) < MIN_AMOUNT && (
                        <motion.p
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ duration: 0.18 }}
                          className="text-yellow-400/85 text-xs flex items-center gap-1.5 bg-yellow-500/10 border border-yellow-500/20 rounded-xl px-3 py-2"
                        >
                          <span>⚠️</span>
                          Minimum amount is ${MIN_AMOUNT} USD
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Continue button */}
                  <div className="pt-1 space-y-2">
                    <motion.div
                      animate={{ opacity: isAmountValid() ? 1 : 0.4 }}
                      transition={{ duration: 0.22 }}
                    >
                      <Button
                        type="button"
                        disabled={!isAmountValid()}
                        onClick={() => goTo("details")}
                        className="w-full h-12 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold rounded-xl shadow-lg shadow-pink-500/25 disabled:shadow-none disabled:from-white/12 disabled:to-white/8 disabled:text-white/35 transition-all"
                      >
                        Continue →
                      </Button>
                    </motion.div>
                    <AnimatePresence>
                      {!isAmountValid() && (
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="text-center text-white/28 text-xs"
                        >
                          Tap an amount to continue
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              )}

              {/* ─── STEP 2: Message + Email ─── */}
              {step === "details" && (
                <motion.div
                  key="details"
                  custom={dir}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={slideT}
                  className="space-y-3"
                >
                  {/* Amount badge */}
                  <div className="flex justify-center">
                    <div className="bg-pink-500/15 border border-pink-400/28 rounded-full px-4 py-1.5 inline-flex items-center gap-1.5">
                      {selectedConfig && <span className="text-base">{selectedConfig.emoji}</span>}
                      <span className="text-pink-300 text-sm font-bold">${finalAmount} USD</span>
                    </div>
                  </div>

                  <div className="text-center space-y-0.5">
                    <h2 className="text-[21px] font-black leading-tight">Almost there! 💌</h2>
                    <p className="text-white/45 text-sm">Add a personal touch to your gift</p>
                  </div>

                  {/* Email — optional, webhook always captures it from Stripe */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between px-0.5">
                      <span className="text-white/65 text-xs font-semibold uppercase tracking-wide">Email</span>
                      <span className="text-white/28 text-[10px] uppercase tracking-wide">Optional</span>
                    </div>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm pointer-events-none select-none">
                        📧
                      </span>
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                        className="pl-10 h-12 bg-white/10 border-2 border-white/20 text-white placeholder:text-white/28 focus:border-blue-400 focus:ring-1 focus:ring-blue-400/25 rounded-xl text-sm"
                      />
                      <AnimatePresence>
                        {formData.email.includes("@") && formData.email.includes(".") && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center"
                          >
                            <Check size={11} strokeWidth={3} />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    <p className="text-white/30 text-[11px] px-1">Stripe will capture your email at checkout for points confirmation</p>
                  </div>

                  {/* Name — optional */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between px-0.5">
                      <span className="text-white/65 text-xs font-semibold uppercase tracking-wide">Name</span>
                      <span className="text-white/28 text-[10px] uppercase tracking-wide">Optional</span>
                    </div>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm pointer-events-none select-none">
                        👤
                      </span>
                      <Input
                        type="text"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                        className="pl-10 h-11 bg-white/10 border-2 border-white/20 text-white placeholder:text-white/28 focus:border-blue-400 focus:ring-1 focus:ring-blue-400/25 rounded-xl text-sm"
                      />
                    </div>
                  </div>

                  {/* Message — optional */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between px-0.5">
                      <span className="text-white/65 text-xs font-semibold uppercase tracking-wide">Message</span>
                      <span className="text-white/28 text-[10px] uppercase tracking-wide">Optional</span>
                    </div>
                    <Textarea
                      placeholder="Write me whatever you want... I'll read every word 💕"
                      value={formData.message}
                      onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                      rows={3}
                      maxLength={500}
                      className="resize-none bg-white/10 border-2 border-white/20 text-white placeholder:text-white/28 focus:border-purple-400 focus:ring-1 focus:ring-purple-400/25 rounded-xl text-sm leading-relaxed"
                    />
                    <p
                      className={`text-right text-[11px] ${
                        formData.message.length > 450 ? "text-yellow-400" : "text-white/25"
                      }`}
                    >
                      {formData.message.length}/500
                    </p>
                  </div>

                  {/* Continue to review */}
                  <Button
                    type="button"
                    onClick={() => goTo("review")}
                    className="w-full h-12 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold rounded-xl shadow-lg shadow-pink-500/25 mt-1"
                  >
                    See my gift →
                  </Button>
                </motion.div>
              )}

              {/* ─── STEP 3: Review (gift card preview) ─── */}
              {step === "review" && (
                <motion.div
                  key="review"
                  custom={dir}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={slideT}
                  className="space-y-4"
                >
                  <div className="text-center space-y-0.5">
                    <h2 className="text-[21px] font-black leading-tight">Your gift 🎁</h2>
                    <p className="text-white/45 text-sm">This is how {BRAND_NAME} will receive it</p>
                  </div>

                  {/* Gift card visual */}
                  <motion.div
                    initial={{ scale: 0.92, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 220, damping: 20 }}
                    className="relative bg-gradient-to-br from-pink-500/18 via-purple-600/12 to-pink-500/10 border-2 border-pink-400/35 rounded-3xl p-6 overflow-hidden shadow-xl shadow-pink-500/10"
                  >
                    {/* Shimmer sweep */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/6 to-transparent"
                      animate={{ x: ["-100%", "200%"] }}
                      transition={{ duration: 3.5, repeat: Infinity, ease: "linear" }}
                    />
                    {/* Ribbon decoration lines */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-full bg-white/5" />
                    <div className="absolute top-1/2 left-0 -translate-y-1/2 w-full h-px bg-white/5" />

                    <div className="relative z-10 text-center space-y-2">
                      <p className="text-pink-300/65 text-[10px] font-bold uppercase tracking-widest">
                        🎀 For {BRAND_NAME}
                      </p>

                      {/* Emoji */}
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
                        className="text-4xl leading-none py-1"
                      >
                        {selectedConfig?.emoji ?? "💝"}
                      </motion.div>

                      {/* Amount */}
                      <motion.div
                        initial={{ scale: 0.7, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.12, type: "spring" }}
                      >
                        <span className="text-5xl font-black text-white tracking-tight">
                          ${finalAmount}
                        </span>
                        <span className="text-white/35 text-base ml-1.5">USD</span>
                      </motion.div>

                      {formData.name && (
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.3 }}
                          className="text-white/38 text-xs italic pt-1"
                        >
                          from {formData.name}
                        </motion.p>
                      )}
                    </div>
                  </motion.div>

                  {/* Message preview */}
                  {formData.message && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.22 }}
                      className="bg-purple-500/10 border border-purple-400/22 rounded-2xl p-4 space-y-1.5"
                    >
                      <p className="text-purple-300/65 text-[10px] font-bold uppercase tracking-wider">
                        ✨ Your message
                      </p>
                      <p className="text-white/85 text-sm leading-relaxed line-clamp-4">
                        &ldquo;{formData.message}&rdquo;
                      </p>
                    </motion.div>
                  )}

                  {/* Pay CTA */}
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="space-y-3 pt-1"
                  >
                    <Button
                      type="button"
                      disabled={processing}
                      onClick={handleConfirm}
                      className="relative w-full h-14 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-[15px] rounded-xl shadow-xl shadow-pink-500/30 overflow-hidden disabled:opacity-60"
                    >
                      {processing ? (
                        <span className="flex items-center justify-center gap-2.5">
                          <Loader2 size={18} className="animate-spin" />
                          Preparing your gift...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          🔒 Pay ${finalAmount} USD
                        </span>
                      )}
                      {!processing && (
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent"
                          animate={{ x: [-200, 600] }}
                          transition={{ duration: 2.8, repeat: Infinity, ease: "linear" }}
                        />
                      )}
                    </Button>

                    <div className="flex items-center justify-center gap-1.5">
                      <span className="text-green-400 text-xs">🔒</span>
                      <span className="text-white/35 text-xs">Secure payment with Stripe</span>
                    </div>

                    <p className="text-center text-[10px] text-white/25">
                      By continuing, you accept our{" "}
                      <a
                        href="/terminos-y-condiciones"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-white/45 transition-colors"
                      >
                        terms and conditions
                      </a>
                    </p>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
